import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { RefreshCw, AlertTriangle, Loader2, Mail, Power, Settings as Cog, X } from "lucide-react";

/**
 * Compact status + manual-poll panel for the IMAP-backed PO inbox.
 * Polls /api/purchase-orders/inbox/status every 15s.
 */
export default function InboxConnectionPanel({ onPolled }) {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [err, setErr] = useState("");
  const [showConfig, setShowConfig] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/purchase-orders/inbox/status"); setStatus(data); setErr(""); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/purchase-orders/inbox/poll-now");
      if (data.status === "ok") {
        toast.success(
          `Polled OK — ${data.inserted} new, ${data.skipped_duplicates} duplicates, ${data.failed} failed.`
        );
        onPolled?.(data);
      } else if (data.status === "skipped") {
        toast.error("IMAP is not configured. Set IMAP_PASSWORD in backend/.env.");
      } else if (data.status === "error") {
        toast.error(`Poll failed: ${data.error}`);
      } else if (data.status === "busy") {
        toast.error("A poll is already in progress.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPolling(false);
    }
  };

  if (!status) return null;

  const cfg = status.configured;
  const hasError = Boolean(status.last_error);
  const inBackoff = Boolean(status.backoff_until) && new Date(status.backoff_until) > new Date();
  const isHealthy = cfg && !hasError && !inBackoff;

  // Healthy → no banner at all (just expose the modal trigger via the parent).
  // Caller can read the status via context if they need more detail.
  if (isHealthy) {
    return (
      <>
        {showConfig && (
          <ImapConfigModal
            initial={status}
            onClose={() => setShowConfig(false)}
            onSaved={(probe) => { setShowConfig(false); load(); if (probe?.status === "ok") onPolled?.(probe); }}
          />
        )}
      </>
    );
  }

  const toneClasses = !cfg
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : "bg-red-50 border-red-200 text-red-900";
  const Icon = !cfg ? Power : AlertTriangle;
  const iconColor = !cfg ? "text-amber-600" : "text-red-600";
  const lastPoll = status.last_poll_at ? timeAgo(status.last_poll_at) : "never";

  return (
    <div className={`border rounded-xl px-4 py-3 ${toneClasses}`} data-testid="po-inbox-status-panel">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <Mail className="w-3.5 h-3.5 inline" />
            <span>IMAP inbox: {status.username || "—"}</span>
            <span className="font-mono text-xs opacity-70">@ {status.host || "—"}</span>
            {!cfg && (
              <span className="text-xs font-medium bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">
                Polling off
              </span>
            )}
            {cfg && hasError && (
              <span className="text-xs font-medium bg-red-200 text-red-900 px-2 py-0.5 rounded-full">
                Connection error
              </span>
            )}
          </div>
          <div className="text-xs mt-1 opacity-90">
            Last poll: <strong>{lastPoll}</strong>
            <span className="opacity-50"> · </span>
            Processed this session: <strong>{status.total_polled}</strong>
            {status.total_failed > 0 && (
              <>
                <span className="opacity-50"> · </span>
                <strong className="text-red-700">{status.total_failed} failed</strong>
              </>
            )}
          </div>
          {hasError && (
            <div className="text-xs mt-1 font-mono opacity-90 break-all">
              {status.last_error}
            </div>
          )}
          {inBackoff && (
            <div className="text-xs mt-2 flex flex-wrap items-center gap-2">
              <span>
                ⏱ Cooling down to avoid the mail server's fail2ban — auto-resumes at{" "}
                <strong>{new Date(status.backoff_until).toLocaleTimeString()}</strong>
                {status.consecutive_auth_failures > 1 && (
                  <span className="opacity-70"> ({status.consecutive_auth_failures} consecutive failures)</span>
                )}
              </span>
              <button
                data-testid="po-inbox-reset-backoff"
                onClick={async () => {
                  try {
                    await api.post("/purchase-orders/inbox/reset-backoff");
                    toast.success("Backoff cleared. Click refresh (↻) to retry.");
                    await load();
                  } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
                }}
                className="text-[11px] font-semibold underline underline-offset-2 hover:text-amber-950"
              >
                Reset backoff
              </button>
            </div>
          )}
        </div>
        <button
          data-testid="po-inbox-configure"
          onClick={() => setShowConfig(true)}
          className="es-btn-secondary flex-shrink-0"
          title="Configure IMAP credentials"
        >
          <Cog className="w-4 h-4" /> Configure
        </button>
      </div>
      {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
      {showConfig && (
        <ImapConfigModal
          initial={status}
          onClose={() => setShowConfig(false)}
          onSaved={(probe) => { setShowConfig(false); load(); if (probe?.status === "ok") onPolled?.(probe); }}
        />
      )}
    </div>
  );
}

function timeAgo(iso) {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return d.toLocaleString();
}

function ImapConfigModal({ initial, onClose, onSaved }) {
  const toast = useToast();
  const [host, setHost] = useState(initial?.host || "mail.edenstone.com.au");
  const [username, setUsername] = useState(initial?.username || "accounts@edenstone.com.au");
  const [password, setPassword] = useState("");
  const [port, setPort] = useState(143);
  const [useStarttls, setUseStarttls] = useState(true);
  const [folder, setFolder] = useState("INBOX");
  const [pollSeconds, setPollSeconds] = useState(Math.max(60, initial?.interval_seconds || 300));
  const [enabled, setEnabled] = useState(Boolean(initial?.configured));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [err, setErr] = useState("");

  const _buildBody = () => {
    const body = {
      host: host.trim(),
      username: username.trim(),
      port,
      use_starttls: useStarttls,
      use_ssl: !useStarttls,
      folder: folder.trim(),
      poll_seconds: pollSeconds,
      enabled,
    };
    if (password) body.password = password;
    return body;
  };

  const test = async () => {
    setTesting(true); setErr(""); setTestResult(null);
    try {
      const { data } = await api.post("/purchase-orders/inbox/test", _buildBody());
      setTestResult(data);
      if (data.ok) toast.success(`Login OK as ${data.used_username} — ${data.unseen_count} unread / ${data.total_count} total in ${data.folder}.`);
      else toast.error(`${data.stage} failed: ${data.error}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true); setErr(""); setResult(null); setTestResult(null);
    try {
      const { data } = await api.post("/purchase-orders/inbox/config", _buildBody());
      setResult(data);
      if (data.probe?.status === "ok") {
        toast.success(`Connected — ${data.probe.inserted} new PO(s) fetched.`);
        onSaved?.(data.probe);
      } else if (data.probe?.status === "error") {
        setErr(data.probe.error);
        toast.error("Could not connect — see error below.");
      } else {
        toast.success("Saved.");
        onSaved?.(data.probe);
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-100">
          <div>
            <h3 className="font-display text-lg font-bold text-zinc-900">Configure IMAP Inbox</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Credentials are stored server-side in <code>backend/.env</code> only.</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-lg ring-1 ring-zinc-200 bg-zinc-50">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-brand-600"
              data-testid="imap-enabled-toggle"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-zinc-900">Enable IMAP polling</div>
              <div className="text-xs text-zinc-500 mt-0.5">Background poll runs every {Math.round(pollSeconds/60)} min. Off by default — keep off until your mail server has whitelisted the production IP.</div>
            </div>
          </label>
          <div>
            <label className="es-label">Host</label>
            <input className="es-input" value={host} onChange={(e) => setHost(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="es-label">Port</label>
              <input type="number" className="es-input" value={port} onChange={(e) => setPort(parseInt(e.target.value || "143", 10))} />
            </div>
            <div>
              <label className="es-label">Encryption</label>
              <select className="es-select w-full" value={useStarttls ? "starttls" : "ssl"} onChange={(e) => setUseStarttls(e.target.value === "starttls")}>
                <option value="starttls">STARTTLS (port 143)</option>
                <option value="ssl">SSL/TLS (port 993)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="es-label">Username</label>
            <input className="es-input" autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="es-label">Password</label>
            <input
              type="password"
              autoComplete="new-password"
              className="es-input"
              placeholder="•••••••••• (kept server-side, never echoed back)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="imap-password-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Leave blank to keep the existing password.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="es-label">Folder</label>
              <input className="es-input" value={folder} onChange={(e) => setFolder(e.target.value)} />
            </div>
            <div>
              <label className="es-label">Poll every (sec)</label>
              <input type="number" min={60} className="es-input" value={pollSeconds} onChange={(e) => setPollSeconds(parseInt(e.target.value || "300", 10))} />
            </div>
          </div>
          {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 rounded-lg font-mono break-all">{err}</div>}
          {testResult && !testResult.ok && (
            <div className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 rounded-lg">
              <div className="font-semibold mb-1">Test failed at stage: <code>{testResult.stage}</code></div>
              <div className="font-mono break-all">{testResult.error}</div>
              {testResult.used_username && <div className="mt-1 opacity-70">Tried username: <code>{testResult.used_username}</code></div>}
            </div>
          )}
          {testResult?.ok && (
            <div className="border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 rounded-lg">
              ✅ Login OK as <code>{testResult.used_username}</code> — {testResult.unseen_count} unread / {testResult.total_count} total in <code>{testResult.folder}</code>. Click <strong>Save & test connection</strong> to persist + ingest.
            </div>
          )}
          {result?.probe?.status === "ok" && (
            <div className="border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 rounded-lg">
              Connection OK — fetched {result.probe.fetched} message(s), inserted {result.probe.inserted} draft PO(s).
            </div>
          )}
          <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600 rounded-lg leading-relaxed">
            <strong>Tip:</strong> if you see <code>AUTHENTICATIONFAILED</code>, try the username variants —
            sometimes the server wants just <code>accounts</code> (local-part only) instead of the full email.
            Click <strong>Test only</strong> to probe without saving.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-100 flex flex-wrap justify-end gap-3">
          <button onClick={onClose} className="es-btn-secondary">Cancel</button>
          <button onClick={test} disabled={testing || saving} className="es-btn-secondary" data-testid="imap-config-test">
            {testing ? "Testing…" : "Test only"}
          </button>
          <button onClick={save} disabled={saving || testing} className="es-btn-primary" data-testid="imap-config-save">
            {saving ? "Saving…" : "Save & test connection"}
          </button>
        </div>
      </div>
    </div>
  );
}
